/**
 * Admin-side hooks for the support-tickets feature.
 *
 * Endpoints (all gated by the STAFF_USER_IDS allowlist in the leads worker):
 *   GET   /api/admin/support/tickets
 *   GET   /api/admin/support/tickets/unread-count
 *   GET   /api/admin/support/tickets/:id
 *   POST  /api/admin/support/tickets/:id/messages
 *   POST  /api/admin/support/tickets/:id/read
 *   PATCH /api/admin/support/tickets/:id
 *
 * The user-side hooks live in features/support/use-support.ts. Shared type
 * names (status / category / priority) are re-exported here so admin pages
 * only need a single import.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'
import type {
  SupportTicket,
  SupportTicketStatus,
  SupportTicketCategory,
  SupportTicketPriority,
  SupportTicketMessage,
} from '@/features/support/use-support'

export type {
  SupportTicket,
  SupportTicketStatus,
  SupportTicketCategory,
  SupportTicketPriority,
  SupportTicketMessage,
}

// Admin views include joined user metadata (email / name / plan).
export type AdminSupportTicket = SupportTicket & {
  readonly user_email: string | null
  readonly user_full_name: string | null
  readonly user_plan: string | null
  readonly message_count: number
}

export type AdminSupportMessage = SupportTicketMessage & {
  readonly sender_email: string | null
  readonly sender_full_name: string | null
}

export type AdminSupportDetail = {
  readonly ticket: AdminSupportTicket
  readonly messages: readonly AdminSupportMessage[]
}

export const adminSupportKeys = {
  all: ['admin', 'support'] as const,
  list: (filters: { status?: string; priority?: string; includeClosed?: boolean }) =>
    [...adminSupportKeys.all, 'list', filters] as const,
  detail: (id: string) => [...adminSupportKeys.all, 'detail', id] as const,
  unread: () => [...adminSupportKeys.all, 'unread-count'] as const,
}

const LIST_POLL_MS = 20_000
const DETAIL_POLL_MS = 10_000
const UNREAD_POLL_MS = 30_000

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

type ListFilters = {
  status?: SupportTicketStatus
  priority?: SupportTicketPriority
  includeClosed?: boolean
}

export function useAdminSupportTickets(filters: ListFilters = {}) {
  const api = useApi()
  return useQuery({
    queryKey: adminSupportKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.status) params.set('status', filters.status)
      if (filters.priority) params.set('priority', filters.priority)
      if (filters.includeClosed) params.set('include_closed', 'true')
      const qs = params.toString()
      const path = qs ? `/api/admin/support/tickets?${qs}` : '/api/admin/support/tickets'
      const res = await api<{ tickets: AdminSupportTicket[] }>(path)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load tickets')
      return res.data.tickets
    },
    refetchInterval: LIST_POLL_MS,
  })
}

export function useAdminSupportTicket(ticketId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ticketId ? adminSupportKeys.detail(ticketId) : ['admin', 'support', 'detail', 'null'],
    queryFn: async () => {
      if (!ticketId) throw new Error('No ticket id')
      const res = await api<AdminSupportDetail>(`/api/admin/support/tickets/${ticketId}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
    enabled: Boolean(ticketId),
    refetchInterval: DETAIL_POLL_MS,
  })
}

export function useAdminSupportUnread() {
  const api = useApi()
  return useQuery({
    queryKey: adminSupportKeys.unread(),
    queryFn: async () => {
      const res = await api<{ unread: number }>('/api/admin/support/tickets/unread-count')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.unread
    },
    refetchInterval: UNREAD_POLL_MS,
  })
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

type StaffReplyInput = {
  readonly ticketId: string
  readonly body: string
  readonly setStatus?: SupportTicketStatus
}

export function useAdminReplyToTicket() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: StaffReplyInput) => {
      const res = await api<{ message: AdminSupportMessage; new_status: SupportTicketStatus }>(
        `/api/admin/support/tickets/${input.ticketId}/messages`,
        {
          method: 'POST',
          body: { body: input.body, set_status: input.setStatus },
        },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to send reply')
      return res.data
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: adminSupportKeys.detail(vars.ticketId) })
      void queryClient.invalidateQueries({ queryKey: adminSupportKeys.all })
    },
  })
}

type UpdateTicketInput = {
  readonly ticketId: string
  readonly status?: SupportTicketStatus
  readonly priority?: SupportTicketPriority
  readonly category?: SupportTicketCategory
}

export function useAdminUpdateTicket() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateTicketInput) => {
      const body: Record<string, string> = {}
      if (input.status) body.status = input.status
      if (input.priority) body.priority = input.priority
      if (input.category) body.category = input.category
      const res = await api<{ ticket: AdminSupportTicket }>(
        `/api/admin/support/tickets/${input.ticketId}`,
        { method: 'PATCH', body },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to update ticket')
      return res.data.ticket
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: adminSupportKeys.detail(vars.ticketId) })
      void queryClient.invalidateQueries({ queryKey: adminSupportKeys.all })
    },
  })
}

export function useAdminMarkTicketRead() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ticketId: string) => {
      const res = await api<unknown>(`/api/admin/support/tickets/${ticketId}/read`, {
        method: 'POST',
      })
      if (!res.success) throw new Error(res.error ?? 'Failed')
    },
    onSuccess: (_v, ticketId) => {
      void queryClient.invalidateQueries({ queryKey: adminSupportKeys.detail(ticketId) })
      void queryClient.invalidateQueries({ queryKey: adminSupportKeys.all })
    },
  })
}
